import { datadogRum } from '@datadog/browser-rum'
import { BrowserMonitoringService } from '@kilohealth/web-app-monitoring/dist/browser'

import { getBrand } from './brand.util'
import { beforeSend, beforeSendRum } from './datadog'

const IS_PRODUCTION = process.env.NODE_ENV === 'production'

const remoteMonitoringServiceParams = {
  serviceVersion: process.env.NEXT_PUBLIC_MONITORING_TOOL__SERVICE_VERSION,
  serviceEnv: process.env.NEXT_PUBLIC_MONITORING_TOOL__SERVICE_ENV,
  serviceName: process.env.NEXT_PUBLIC_MONITORING_TOOL__SERVICE_NAME,
}

const initDataDogRum = () => {
  datadogRum.init({
    applicationId: process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID as string,
    clientToken: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN as string,
    site: 'datadoghq.com',
    service: process.env.NEXT_PUBLIC_MONITORING_TOOL__SERVICE_NAME,
    env: process.env.NEXT_PUBLIC_MONITORING_TOOL__SERVICE_ENV,
    version: process.env.NEXT_PUBLIC_MONITORING_TOOL__SERVICE_VERSION,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 20,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
    beforeSend: beforeSendRum,
  })

  datadogRum.setGlobalContextProperty('brand', getBrand().website)
}

export let monitoring: BrowserMonitoringService | undefined

const createMonitoring = () => {
  if (!IS_PRODUCTION || typeof window === 'undefined') {
    return undefined
  }

  initDataDogRum()

  return new BrowserMonitoringService(
    {
      authToken: process.env.NEXT_PUBLIC_MONITORING_TOOL__CLIENT_TOKEN,
      ...remoteMonitoringServiceParams,
    },
    {
      sessionSampleRate: 100,
      forwardConsoleLogs: ['error'],
      beforeSend,
    },
  )
}

monitoring = createMonitoring()

type CaptureContext = {
  tags?: Record<string, unknown>
  extra?: Record<string, unknown>
  level?: string
}

const toError = (e: unknown): Error => {
  if (e instanceof Error) return e

  if (typeof e === 'object' && e !== null) {
    try {
      const seen = new WeakSet()
      const serialized = JSON.stringify(
        e,
        (_key, value) => {
          if (typeof value === 'object' && value !== null) {
            if (seen.has(value)) return '[Circular]'
            seen.add(value)
          }
          return value
        },
        2,
      )
      return new Error(serialized)
    } catch {
      return new Error((e as any)?.message ?? 'Unknown error')
    }
  }

  return new Error(String(e))
}

export const captureException = (e: unknown, context?: CaptureContext) => {
  if (!monitoring || !IS_PRODUCTION) {
    console.error(e, context)
    return
  }

  const error = toError(e)
  monitoring.error(error.message || 'Unknown error', { _custom: context }, error)
}

export const captureMessage = (message: string, context?: CaptureContext) => {
  if (!monitoring || !IS_PRODUCTION) {
    console.error(message, context)
    return
  }

  monitoring.error(message, { _custom: context }, new Error(message))
}

export const setUser = (user: { id: string; email?: string }) => {
  if (!IS_PRODUCTION) return
  datadogRum.setUser({ id: user.id, email: user.email })
}

export const clearUser = () => {
  if (!IS_PRODUCTION) return
  datadogRum.clearUser()
}
